import React, { useEffect } from "react";
import MagneticButton from "../components/site/ui/MagneticButton";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { trackEvent, trackReciprocity } from "../lib/track";

const LOGO = "/assets/logo.png";

const getSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
};

const BlueprintPreviewPage = () => {
  const sessionId = getSessionId();

  useEffect(() => {
    trackEvent("blueprint_direction_preview_viewed", { session_id: sessionId });
    trackReciprocity("blueprint_direction_preview", {
      related_session_id: sessionId,
      related_product_type: "blueprint",
    });
  }, [sessionId]);

  return (
    <div data-testid="blueprint-preview-page" className="min-h-screen bg-themed text-themed relative overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative max-w-3xl text-center py-20">
        <div className="flex justify-center mb-10">
          <img src={LOGO} alt="Authentic Momentum" className="w-24 h-24 object-contain logo-mask animate-breathe-premium" draggable="false" />
        </div>

        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">First Signal</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
          Your Direction Preview
        </h1>
        <p className="mt-6 text-themed-muted leading-relaxed">
          Your answers have been received. A fuller Personal Action Plan can now be reviewed, but your first signal is already visible.
        </p>
        <p className="mt-3 text-themed-muted text-sm leading-relaxed italic">
          This is not the complete Personal Action Plan. This is the first reflection from the field of answers you just gave.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
          <div className="glass-card p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-3">Primary Clarity</p>
            <p className="text-themed-muted leading-relaxed text-sm">
              Based on your answers, your current focus appears to be connected to what you are building, becoming, clarifying, serving, offering, or repositioning.
            </p>
          </div>
          <div className="glass-card p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-3">Energy Pattern</p>
            <p className="text-themed-muted leading-relaxed text-sm">
              You may be moving from a pattern of force toward a clearer pattern of alignment.
            </p>
          </div>
          <div className="glass-card p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-3">Next Aligned Move</p>
            <p className="text-themed-muted leading-relaxed text-sm">
              Your next step is likely to simplify the path in front of you and name the one action that carries the most honest energy.
            </p>
          </div>
        </div>

        <div className="mt-12 glass-card p-8 text-left max-w-2xl mx-auto">
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">Reflection</p>
          <p className="font-serif italic text-2xl text-themed leading-snug">
            What is the one move that feels both honest and slightly uncomfortable?
          </p>
        </div>

        <p className="mt-10 text-themed-muted italic">
          This is the first signal. The deeper synthesis begins from here.
        </p>

        <div className="mt-6 glass-card p-6 max-w-2xl mx-auto text-sm text-themed-muted leading-relaxed">
          Cyrus will review your answers and synthesize your complete
          Authentic Momentum Personal Action Plan. You will receive the completed
          Personal Action Plan after review.
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <MagneticButton
            data-testid="preview-return-home"
            onClick={() => (window.location.href = "/")}
            className="btn-gold"
          >
            Return Home
          </MagneticButton>
          <button
            type="button"
            data-testid="preview-book-orientation-reset"
            onClick={() => {
              trackEvent("blueprint_preview_orientation_reset_cta_clicked");
              window.dispatchEvent(
                new CustomEvent("am:open-orientation-reset-checkout")
              );
            }}
            className="btn-ghost-gold"
          >
            Book the Clarity Reset Session · $149
          </button>
        </div>
      </div>
      <OrientationResetCheckoutHost />
    </div>
  );
};

export default BlueprintPreviewPage;
